import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { Badge } from "@/components/ui/badge";

import type { ClosedContractRow, ClosedRollup, ForecastDetailRow } from "../api";
import { SEGMENT_LABEL, type Segment } from "../constants";
import { fmtCents, fmtCentsWhole, fmtMonthName } from "../format";

/**
 * Drill-down for the Summary tab's Closed and Forecast tiles.
 *
 * Quick view = the top contributors; expanded = every row with the full
 * arithmetic. The Closed variant deliberately shows face value alongside the
 * counted value, because a replacement contributing far less than its face
 * value is the single most confusing thing about the closed number — the July
 * Oilstop replacement counts $21,600 against a $150,792 face.
 */
export function ClosedTileDrawer({
  open,
  expanded,
  onOpenChange,
  onExpandedChange,
  onClose,
  periodLabel,
  rollup,
  contracts,
}: {
  open: boolean;
  expanded: boolean;
  onOpenChange: (v: boolean) => void;
  onExpandedChange: (v: boolean) => void;
  onClose: () => void;
  periodLabel: string;
  rollup: ClosedRollup | undefined;
  contracts: ClosedContractRow[];
}) {
  const rows = [...contracts].sort(
    (a, b) => b.revenue_cents - a.revenue_cents,
  );
  const shown = expanded ? rows : rows.slice(0, 10);
  const anyHaircut = rows.some((r) => r.replacement_haircut_cents !== 0);

  return (
    <EntityDrawer
      open={open}
      onOpenChange={onOpenChange}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      onClose={onClose}
      title="Closed — signed contracts"
      subtitle={periodLabel}
    >
      <div className="border-b border-border p-4">
        <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">
          Closed
        </div>
        <div className="text-3xl font-extrabold tracking-tight tabular-nums">
          {fmtCentsWhole(rollup?.total_cents ?? 0)}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <Mini label="New" value={fmtCentsWhole(rollup?.new_cents ?? 0)} />
          <Mini label="CS" value={fmtCentsWhole(rollup?.cs_cents ?? 0)} />
          <Mini
            label="Partner"
            value={fmtCentsWhole(rollup?.partner_cents ?? 0)}
          />
        </div>
      </div>

      <div className="p-4">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {expanded ? `All contracts (${rows.length})` : "Top contracts"}
        </div>
        {rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No contracts signed in this period.
          </p>
        ) : (
          <table className="mt-2 w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="px-2 py-1.5 text-left font-semibold">Account</th>
                {expanded && (
                  <th className="px-2 py-1.5 text-left font-semibold">Type</th>
                )}
                <th className="px-2 py-1.5 text-left font-semibold">Segment</th>
                {expanded && (
                  <th className="px-2 py-1.5 text-left font-semibold">Closer</th>
                )}
                {expanded && anyHaircut && (
                  <th className="px-2 py-1.5 text-right font-semibold">Face</th>
                )}
                <th className="px-2 py-1.5 text-right font-semibold">Closed</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => (
                <tr
                  key={`${c.contract_id}`}
                  className="border-b border-border/60"
                >
                  <td className="px-2 py-1.5">{c.account_name ?? "—"}</td>
                  {expanded && (
                    <td className="px-2 py-1.5 text-xs text-muted-foreground">
                      {c.contract_type_name ?? "—"}
                    </td>
                  )}
                  <td className="px-2 py-1.5">
                    <Badge variant="secondary">
                      {SEGMENT_LABEL[c.segment as Segment] ?? c.segment}
                    </Badge>
                  </td>
                  {expanded && (
                    <td className="px-2 py-1.5 text-xs text-muted-foreground">
                      {c.closer_name ?? "—"}
                    </td>
                  )}
                  {expanded && anyHaircut && (
                    <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
                      {c.replacement_haircut_cents !== 0
                        ? fmtCentsWhole(c.face_revenue_cents)
                        : "—"}
                    </td>
                  )}
                  <td className="px-2 py-1.5 text-right font-semibold tabular-nums">
                    {fmtCentsWhole(c.revenue_cents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!expanded && rows.length > shown.length && (
          <p className="mt-2 text-[10px] text-muted-foreground">
            {rows.length - shown.length} more — expand to see all.
          </p>
        )}
        {anyHaircut && (
          <p className="mt-3 text-[10px] text-muted-foreground">
            Replacement contracts count only their like-for-like increase over
            what the account was already billed, not their face value.
          </p>
        )}
      </div>
    </EntityDrawer>
  );
}

/** Drill-down for the Forecast tile — the per-rep calls behind the headline. */
export function ForecastTileDrawer({
  open,
  expanded,
  onOpenChange,
  onExpandedChange,
  onClose,
  periodLabel,
  totalCents,
  detail,
}: {
  open: boolean;
  expanded: boolean;
  onOpenChange: (v: boolean) => void;
  onExpandedChange: (v: boolean) => void;
  onClose: () => void;
  periodLabel: string;
  totalCents: number;
  detail: ForecastDetailRow[];
}) {
  const rows = [...detail].sort((a, b) => b.call_cents - a.call_cents);
  const shown = expanded ? rows : rows.slice(0, 10);

  return (
    <EntityDrawer
      open={open}
      onOpenChange={onOpenChange}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      onClose={onClose}
      title="Forecast — CRO calls"
      subtitle={periodLabel}
    >
      <div className="border-b border-border p-4">
        <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">
          Forecast
        </div>
        <div className="text-3xl font-extrabold tracking-tight tabular-nums text-success">
          {fmtCentsWhole(totalCents)}
        </div>
        <div className="mt-1 text-[10px] text-muted-foreground">
          Latest call per rep · {rows.length} {rows.length === 1 ? "call" : "calls"}
        </div>
      </div>

      <div className="p-4">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {expanded ? `All calls (${rows.length})` : "Largest calls"}
        </div>
        {rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No forecast calls entered for this period.
          </p>
        ) : (
          <table className="mt-2 w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="px-2 py-1.5 text-left font-semibold">Rep</th>
                <th className="px-2 py-1.5 text-left font-semibold">Segment</th>
                {expanded && (
                  <th className="px-2 py-1.5 text-left font-semibold">Month</th>
                )}
                {expanded && (
                  <th className="px-2 py-1.5 text-left font-semibold">Called</th>
                )}
                <th className="px-2 py-1.5 text-right font-semibold">Call</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r, i) => (
                <tr
                  key={`${r.rep_id ?? "team"}-${r.segment}-${r.month ?? "q"}-${i}`}
                  className="border-b border-border/60"
                >
                  <td className="px-2 py-1.5">{r.rep_name}</td>
                  <td className="px-2 py-1.5">
                    <Badge variant="secondary">
                      {SEGMENT_LABEL[r.segment as Segment] ?? r.segment}
                    </Badge>
                  </td>
                  {expanded && (
                    <td className="px-2 py-1.5 text-xs text-muted-foreground">
                      {r.month ? fmtMonthName(r.month) : "—"}
                    </td>
                  )}
                  {expanded && (
                    <td className="px-2 py-1.5 text-xs tabular-nums text-muted-foreground">
                      {r.iso_year}-W{String(r.iso_week).padStart(2, "0")}
                    </td>
                  )}
                  <td className="px-2 py-1.5 text-right font-semibold tabular-nums">
                    {fmtCents(r.call_cents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!expanded && rows.length > shown.length && (
          <p className="mt-2 text-[10px] text-muted-foreground">
            {rows.length - shown.length} more — expand to see all.
          </p>
        )}
      </div>
    </EntityDrawer>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60">
        {label}
      </div>
      <div className="mt-0.5 font-semibold tabular-nums">{value}</div>
    </div>
  );
}
